/**
 * The `genetics` facet: one animal's registration, latest EPDs per association, DNA status,
 * parentage evidence, breed makeup, contemporary groups and donor or collection totals. Rebuilt from
 * events on every change so corrections and voids stay honest.
 */
import { defineFacet, msg, type Badge, type Fact, type FacetEvent } from "../../../contract";
import type { EvaluationSnapshot } from "../shared/epd";
import { associationKey, associationShort, formatEpd } from "../shared/epd";
import type { DnaResultState, GeneticsState, ParentageAssertion, RegistrationState } from "../shared/types";
import type {
  CollectionData, CompositionData, ContemporaryData, DnaResultData, DnaSampleData, DnaSentData, EvaluationData, FlushData, ParentageData, RegistrationData, TransferData,
} from "./events";

export const FACET_EVENTS = [
  "genetics.parentage", "genetics.pedigree_correction", "genetics.breed_composition", "genetics.evaluation", "genetics.dna_sample", "genetics.dna_sent",
  "genetics.dna_result", "genetics.registration", "genetics.registration_transfer", "genetics.contemporary_group", "genetics.flush", "genetics.semen_collection",
];

const day = (e: FacetEvent) => e.occurred_at?.slice(0, 10) ?? null;

export function snapshotFrom(e: { id: string; occurred_at: string | null; data: EvaluationData }): EvaluationSnapshot {
  const d = e.data;
  return {
    event_id: e.id, association: d.association, kind: d.kind ?? "epd", as_of: e.occurred_at?.slice(0, 10) ?? null, release: d.release, source: d.source ?? "entered", genomic: d.genomic,
    traits: Object.fromEntries((d.traits ?? []).map((t) => [t.trait.trim(), { value: t.value, ...(t.acc !== undefined ? { acc: t.acc } : {}), ...(t.pct !== undefined ? { pct: t.pct } : {}) }])),
    indexes: Object.fromEntries((d.indexes ?? []).map((t) => [t.index.trim(), { value: t.value, ...(t.pct !== undefined ? { pct: t.pct } : {}) }])),
  };
}

export function reduceGenetics(events: FacetEvent[]): GeneticsState | null {
  const s: GeneticsState = {};
  const regs = new Map<string, RegistrationState>();
  const evals = new Map<string, EvaluationSnapshot>();
  for (const e of events) {
    switch (e.type) {
      case "genetics.parentage": {
        if (!e.roles.includes("animal")) break;
        const d = e.data as ParentageData;
        const list = (s.parentage ??= {})[d.role] ?? [];
        const entry: ParentageAssertion = { status: d.status, basis: d.basis, parent_id: d.parent_id, on: day(e), event_id: e.id, ...(d.lab ? { lab: d.lab } : {}) };
        s.parentage[d.role] = [...list.filter((a) => a.parent_id !== d.parent_id), entry];
        break;
      }
      case "genetics.pedigree_correction": {
        if (!e.roles.includes("animal")) break;
        s.corrections = (s.corrections ?? 0) + 1;
        // A correction settles earlier exclusions for that parent role.
        const role = (e.data as { role: "sire" | "dam" }).role;
        const list = s.parentage?.[role];
        if (list) s.parentage![role] = list.map((a) => (a.status === "excluded" ? { ...a, resolved: true } : a));
        break;
      }
      case "genetics.breed_composition": {
        const d = e.data as CompositionData;
        const comps = (d.components ?? []).map((c) => ({ breed: c.breed.trim(), percent: c.percent }));
        const total = comps.reduce((n, c) => n + c.percent, 0);
        s.composition = { components: comps, unknown: Math.max(0, Math.round((100 - total) * 100) / 100), basis: d.basis, on: day(e) };
        break;
      }
      case "genetics.evaluation": {
        const snap = snapshotFrom(e as FacetEvent<EvaluationData>);
        const key = `${associationKey(snap.association)}|${snap.kind}`;
        const prev = evals.get(key);
        if (!prev || (snap.as_of ?? "") >= (prev.as_of ?? "")) evals.set(key, snap);
        break;
      }
      case "genetics.dna_sample": {
        const d = e.data as DnaSampleData;
        s.dna = { status: "collected", on: day(e), ...(d.barcode ? { barcode: d.barcode } : {}), ...(d.lab ? { lab: d.lab } : {}), ...(d.sample_type ? { sample_type: d.sample_type } : {}), ...(d.tests?.length ? { tests: d.tests } : {}) };
        break;
      }
      case "genetics.dna_sent": {
        const d = e.data as DnaSentData;
        s.dna = { ...(s.dna ?? {}), status: "sent", on: day(e), lab: d.lab };
        break;
      }
      case "genetics.dna_result": {
        const d = e.data as DnaResultData;
        const result: DnaResultState = {
          on: day(e), lab: d.lab, sample_ok: d.outcome !== "failed", event_id: e.id,
          ...(d.call_rate !== undefined ? { call_rate: d.call_rate } : {}), ...(d.genomic ? { genomic: true } : {}), ...(d.ge_reference ? { ge_reference: d.ge_reference } : {}),
          ...(d.sire ? { sire: d.sire } : {}), ...(d.dam ? { dam: d.dam } : {}), ...(d.sire_tested_id ? { sire_tested_id: d.sire_tested_id } : {}), ...(d.dam_tested_id ? { dam_tested_id: d.dam_tested_id } : {}),
          conditions: d.conditions ?? [], ...(d.revised ? { revised: true } : {}),
        };
        s.dna_result = result;
        s.dna = { ...(s.dna ?? {}), status: d.outcome === "failed" ? "failed" : "resulted", on: day(e), lab: d.lab, ...(d.barcode && !s.dna?.barcode ? { barcode: d.barcode } : {}) };
        // A lab parentage result is DNA evidence for the tested parent.
        for (const role of ["sire", "dam"] as const) {
          const outcome = d[role];
          const tested = d[`${role}_tested_id` as const] ?? null;
          if (outcome !== "verified" && outcome !== "excluded") continue;
          const list = (s.parentage ??= {})[role] ?? [];
          s.parentage[role] = [...list.filter((a) => a.parent_id !== tested), { status: outcome === "verified" ? "confirmed" : "excluded", basis: "dna", parent_id: tested, on: day(e), event_id: e.id, lab: d.lab }];
        }
        break;
      }
      case "genetics.registration": {
        const d = e.data as RegistrationData;
        const key = associationKey(d.association);
        const prev = regs.get(key);
        // A later "submitted" for a re-registration does not hide a number already issued.
        const number = d.number ?? (d.status !== "rejected" ? prev?.number : undefined);
        regs.set(key, { association: d.association, status: d.status, on: day(e), event_id: e.id, ...(number ? { number } : {}), ...(d.reason ? { reason: d.reason } : {}) });
        break;
      }
      case "genetics.registration_transfer": {
        const d = e.data as TransferData;
        s.transfer = { status: d.status, on: day(e), association: d.association, buyer_id: e.subjects.find((x) => x.role === "buyer")?.id };
        break;
      }
      case "genetics.contemporary_group": {
        const d = e.data as ContemporaryData;
        (s.cg ??= {})[d.stage] = { code: d.code, season: d.season, on: day(e), ...(e.subjects.find((x) => x.role === "group") ? { group_id: e.subjects.find((x) => x.role === "group")!.id } : {}) };
        break;
      }
      case "genetics.flush": {
        if (!e.roles.includes("donor")) break;
        const d = e.data as FlushData;
        const prev = s.donor ?? { flushes: 0, embryos: 0, last_flush: null };
        s.donor = { flushes: prev.flushes + 1, embryos: prev.embryos + (d.frozen ?? 0) + (d.fresh ?? 0), last_flush: day(e) ?? prev.last_flush };
        break;
      }
      case "genetics.semen_collection": {
        if (!e.roles.includes("bull")) break;
        const d = e.data as CollectionData;
        const prev = s.collections ?? { count: 0, straws: 0, last: null };
        s.collections = { count: prev.count + 1, straws: prev.straws + (d.straws ?? 0), last: day(e) ?? prev.last };
        break;
      }
    }
  }
  if (regs.size) {
    s.registrations = [...regs.values()];
    s.registration = [...s.registrations].filter((r) => r.status === "registered").sort((a, b) => (b.on ?? "").localeCompare(a.on ?? ""))[0]
      ?? [...s.registrations].sort((a, b) => (b.on ?? "").localeCompare(a.on ?? ""))[0];
  }
  if (evals.size) {
    s.evaluations = [...evals.values()].sort((a, b) => (b.as_of ?? "").localeCompare(a.as_of ?? ""));
    const latest = s.evaluations.find((x) => x.kind !== "ebv") ?? s.evaluations[0];
    s.epds = { as_of: latest.as_of, association: latest.association, values: Object.fromEntries([...Object.entries(latest.traits), ...Object.entries(latest.indexes)].map(([k, v]) => [k, v.value])) };
  }
  return Object.keys(s).length ? s : null;
}

/** True when DNA or the association excluded a parent (the Today list compares with the recorded link). */
export function excludedParents(s: GeneticsState | null | undefined): Array<{ role: "sire" | "dam"; parent_id: string | null; on: string | null; basis: string; event_id: string }> {
  const out: Array<{ role: "sire" | "dam"; parent_id: string | null; on: string | null; basis: string; event_id: string }> = [];
  for (const role of ["sire", "dam"] as const) for (const a of s?.parentage?.[role] ?? []) if (a.status === "excluded" && !a.resolved) out.push({ role, parent_id: a.parent_id, on: a.on, basis: a.basis, event_id: a.event_id });
  return out;
}

export const geneticsFacet = defineFacet<GeneticsState>({
  id: "genetics",
  subject: "animal",
  version: 1,
  eventTypes: FACET_EVENTS,
  reduce: (events) => reduceGenetics(events),
  badges: (s) => {
    const out: Badge[] = [];
    if (excludedParents(s).length) out.push({ label: msg("Parentage excluded"), tone: "bad", icon: "dna" });
    if (s.registration?.status === "rejected") out.push({ label: msg("Registration rejected"), tone: "warn", icon: "award" });
    else if (s.registration?.status === "pending") out.push({ label: msg("Registration on hold"), tone: "warn", icon: "award" });
    if (s.dna?.status === "failed") out.push({ label: msg("DNA sample failed"), tone: "warn", icon: "dna" });
    return out;
  },
  facts: (s) => {
    const out: Fact[] = [];
    const r = s.registration;
    if (r) {
      const assoc = associationShort(r.association);
      const value = r.status === "registered" ? `${assoc} ${r.number ?? ""}`.trim()
        : r.status === "submitted" ? msg("Sent to {{association}}", { association: assoc })
        : r.status === "rejected" ? msg("Rejected by {{association}}", { association: assoc })
        : r.status === "pending" ? msg("On hold at {{association}}", { association: assoc })
        : msg("Recorded with {{association}}", { association: assoc });
      out.push({ label: "Registration", value, icon: "award", tone: r.status === "rejected" || r.status === "pending" ? "warn" : "genetics" });
    }
    const latest = s.evaluations?.find((x) => x.kind !== "ebv") ?? s.evaluations?.[0];
    if (latest) {
      const keys = ["CED", "BW", "WW", "YW"].filter((k) => latest.traits[k]);
      const shown = (keys.length ? keys : Object.keys(latest.traits).slice(0, 4)).map((k) => `${k} ${formatEpd(latest.traits[k].value)}`);
      if (shown.length) out.push({ label: "EPDs", value: `${associationShort(latest.association)} · ${shown.join(" · ")}`, icon: "report", tone: "genetics" });
    }
    if (s.dna) {
      const dr = s.dna_result;
      const value = s.dna.status === "failed" ? msg("Sample failed, collect again")
        : s.dna.status === "resulted" ? (excludedParents(s).length ? msg("Parentage excluded") : dr?.sire === "verified" ? msg("Sire verified") : msg("Results back"))
        : s.dna.status === "sent" ? (s.dna.lab ? msg("At {{lab}}", { lab: s.dna.lab }) : msg("At the lab"))
        : msg("Sample pulled, not sent");
      out.push({ label: "DNA", value, icon: "dna", tone: s.dna.status === "failed" || excludedParents(s).length ? "warn" : "genetics" });
    }
    if (s.composition?.components.length) {
      const parts = s.composition.components.map((c) => `${Number(c.percent.toFixed(2))}% ${c.breed}`);
      out.push({ label: "Breed makeup", value: s.composition.unknown > 0 ? `${parts.join(", ")} · ${s.composition.unknown}% ?` : parts.join(", "), icon: "layers", tone: "genetics" });
    }
    return out;
  },
  filters: {
    "registration.status": { label: "Registration", type: "enum", options: { registered: "Registered", submitted: "Submitted", rejected: "Rejected", pending: "On hold" } },
    "dna.status": { label: "DNA", type: "enum", options: { collected: "Sample pulled", sent: "At the lab", resulted: "Results back", failed: "Sample failed" } },
  },
});
